import LoaderComponent from 'components/LoaderComponent';
import {
  EntriesConsumerRenderProps,
  ExtendedBaseEntryDto,
  MedicamentChanges,
  withEntriesContext
} from 'components/segments/entires/EntriesContext';
import _ from 'lodash';
import React, { Component } from "react";
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Icon, Table } from 'semantic-ui-react';
import styled from 'styled-components';
import { ProcedureCategory, FavoriteEntryType } from 'ts-types/api.enums';
import { BaseProcedureResult, SearchProceduresResult } from 'ts-types/api.types';
import { favoriteKey, FavoritesMap } from 'utils/favoriteUtils';

const ScrollableTableWrapper = styled.div`
  height: 445px;
  overflow-y: auto;
  width: 100%;
  border: 1px solid rgba(34, 36, 38, .15);

  & .ui.table thead tr:first-child > th {
    position: sticky !important;
    top: 0;
    z-index: 2;
    background: #fff;
  }

  & .ui.table {
    border: 0 !important;
  }
`;

interface Props extends MessageSourceProps, EntriesConsumerRenderProps {
  proceduresLoading: boolean,
  procedureCategory: ProcedureCategory,
  procedures: Array<Partial<SearchProceduresResult>>,
  onSelectProcedure: (procedure: Partial<SearchProceduresResult>) => void,
  setSelectedProcedure: (selectedProcedure?: number) => void,
  // when set, a star column is shown and the results can be toggled as favorites of this entry type
  favoriteEntryType?: FavoriteEntryType,
  favorites?: FavoritesMap,
  onToggleFavorite?: (procedure: Partial<SearchProceduresResult>) => void
}

interface State {
  selectedProcedure?: number
}

class ProceduresTableSegment extends Component<Props, State> {

  state = {
    selectedProcedure: -1
  };

  shouldComponentUpdate(
      {
        proceduresLoading: nextProceduresLoading,
        procedures: nextProcedures,
        procedureCategory: nextProcedureCategory,
        currentEntries: nextCurrentEntries,
        favoriteEntryType: nextFavoriteEntryType,
        favorites: nextFavorites
      }: Readonly<Props>,
      {
        selectedProcedure: nextSelectedProcedure
      }: Readonly<State>,
      nextContext: any): boolean {

    const {
      proceduresLoading,
      procedures,
      currentEntries,
      favoriteEntryType,
      favorites
    } = this.props;

    const {selectedProcedure} = this.state;

    if (nextSelectedProcedure != selectedProcedure) {
      return true;
    }

    if (nextFavoriteEntryType != favoriteEntryType || nextFavorites !== favorites) {
      return true;
    }

    if (nextProceduresLoading != proceduresLoading) {

      this.setState({
        selectedProcedure: -1
      });

      return true;
    }

    const isMedicamentOrMaterial = ProcedureCategory.MEDICAMENT === nextProcedureCategory
        || ProcedureCategory.MATERIAL === nextProcedureCategory;
    if (isMedicamentOrMaterial || this.hasMedicamentQuantityChange(currentEntries, nextCurrentEntries)) {
      return true;
    }

    const isCostEstimation = ProcedureCategory.COST_ESTIMATION === nextProcedureCategory;
    if (isCostEstimation && this.hasCostEstimationQuantityChange(procedures, nextProcedures)) {
      return true;
    }

    return false;
  }

  hasMedicamentQuantityChange = (
      currentEntries: ExtendedBaseEntryDto[],
      nextCurrentEntries: ExtendedBaseEntryDto[]): boolean => {

    const medicamentPredicate = (item: ExtendedBaseEntryDto) => !!item.medicamentId;

    const meds = currentEntries.filter(medicamentPredicate);
    const medsMap = _.keyBy(meds, "medicamentId");

    const nextMeds = nextCurrentEntries.filter(medicamentPredicate);
    const nextMedsMap = _.keyBy(nextMeds, "medicamentId");

    const deletedItem = meds.find(
        med => !!nextMedsMap[med.medicamentId]);
    if (deletedItem) {
      return true;
    }

    const addedOrChangedItem = nextMeds.find(
        nextMed => {
          const currentMed = medsMap[nextMed.medicamentId];
          return !currentMed || currentMed.quantity !== nextMed.quantity;
        });
    return !!addedOrChangedItem;
  }

  hasCostEstimationQuantityChange = (
      procedures: Partial<SearchProceduresResult>[],
      nextProcedures: Partial<SearchProceduresResult>[]
  ): boolean => {

    let quantityDidUpdate: boolean = false;

    for (let i = 0; i < procedures.length; i++) {

      if (procedures[i].childProcedures) {

        const childProcedures: BaseProcedureResult[] = procedures[i].childProcedures!;
        const nextChildProcedures: BaseProcedureResult[] = nextProcedures[i].childProcedures!;

        for (let j = 0; j < childProcedures.length; j++) {
          if (!nextChildProcedures[j] || childProcedures[j].quantity != nextChildProcedures[j].quantity) {
            quantityDidUpdate = true;
            break;
          }
        }
      }

      if (!nextProcedures[i] || procedures[i].quantity != nextProcedures[i].quantity) {
        quantityDidUpdate = true;
        break;
      }
    }

    return quantityDidUpdate;
  }

  markProcedure = (procedureId?: number) => {

    const {selectedProcedure} = this.state;
    const {setSelectedProcedure} = this.props;

    if (selectedProcedure != procedureId) {
      this.setState({
        selectedProcedure: procedureId
      });
      setSelectedProcedure(procedureId);
    }
  };

  render(): React.ReactNode {

    const {
      proceduresLoading,
      procedureCategory,
      getMessage
    } = this.props;

    return (
        <ScrollableTableWrapper>
          <Table celled selectable>
            <Table.Header>
              {this.renderTableHeader(procedureCategory)}
            </Table.Header>
            <Table.Body>
              {this.renderTableBody(procedureCategory)}
              {proceduresLoading && (
                  <Table.Row>
                    <Table.Cell>
                      <LoaderComponent
                          message={`${getMessage("loading")} ${getMessage("procedures").toLowerCase()}`}
                          dimmerProps={{active: true, inverted: false}}
                      />
                    </Table.Cell>
                  </Table.Row>
              )}
            </Table.Body>
          </Table>
        </ScrollableTableWrapper>
    );
  };

  /** Header cell of the favorite star column, or undefined when favorites are not available. */
  renderFavoriteHeaderCell = (): React.ReactNode | undefined => {

    const {favoriteEntryType, getMessage} = this.props;

    if (!favoriteEntryType) {
      return undefined;
    }

    return (
        <Table.HeaderCell verticalAlign={"middle"} textAlign="center" collapsing key={0}>
          <Icon name="star" title={getMessage("favorites")}/>
        </Table.HeaderCell>
    );
  };

  /** Cell with the favorite star of a result row (filled = favorite, outline = not a favorite). */
  renderFavoriteCell = (procedure: Partial<SearchProceduresResult>): React.ReactNode | undefined => {

    const {favoriteEntryType, favorites = {}, onToggleFavorite, getMessage} = this.props;

    if (!favoriteEntryType) {
      return undefined;
    }

    const favorite = !!favorites[favoriteKey(favoriteEntryType, procedure.id!)];

    // the click must neither select the row nor (on double click) add the entry
    const onClick = (event: React.MouseEvent) => {
      event.stopPropagation();

      if (onToggleFavorite) {
        onToggleFavorite(procedure);
      }
    };

    return (
        <Table.Cell
            key={0}
            textAlign="center"
            collapsing
            onClick={onClick}
            onDoubleClick={(event: React.MouseEvent) => event.stopPropagation()}
        >
          <Icon
              link
              name={favorite ? "star" : "star outline"}
              color={favorite ? "yellow" : undefined}
              title={getMessage(favorite ? "favorites.remove" : "favorites.add")}
          />
        </Table.Cell>
    );
  };

  renderTableHeader = (procedureType?: ProcedureCategory): React.ReactNode => {

    const {
      getMessage
    } = this.props;

    let headerColumns: Array<React.ReactNode>;

    switch (procedureType) {
      case ProcedureCategory.PROCEDURE: {
        headerColumns = [
          this.renderFavoriteHeaderCell(),
          <Table.HeaderCell verticalAlign={"middle"}
                            key={1}>{getMessage("code")}</Table.HeaderCell>,
          <Table.HeaderCell verticalAlign={"middle"}
                            key={2}>{getMessage("procedure.internalDescription")}</Table.HeaderCell>
        ];

        break;
      }
      case ProcedureCategory.MEDICAMENT:
      case ProcedureCategory.MATERIAL: {
        headerColumns = [
          this.renderFavoriteHeaderCell(),
          <Table.HeaderCell verticalAlign={"middle"} key={1}>{getMessage("code")}</Table.HeaderCell>,
          <Table.HeaderCell verticalAlign={"middle"} key={2}>{getMessage("description")}</Table.HeaderCell>,
          <Table.HeaderCell verticalAlign={"middle"} key={3}>{getMessage("procedure.quantity.short")}</Table.HeaderCell>
        ];

        break;
      }
      case ProcedureCategory.BLOCK_PROCEDURE: {
        headerColumns = [
          this.renderFavoriteHeaderCell(),
          <Table.HeaderCell verticalAlign={"middle"} key={1}>{getMessage("description")}</Table.HeaderCell>
        ];

        break;
      }
      case ProcedureCategory.COST_ESTIMATION: {
        headerColumns = [
          <Table.HeaderCell verticalAlign={"middle"}
                            key={1}>{getMessage("procedure.quantity.short")}</Table.HeaderCell>,
          <Table.HeaderCell verticalAlign={"middle"}
                            key={2}>{getMessage("description")}</Table.HeaderCell>
        ];

        break;
      }
      default: {
        headerColumns = [
          this.renderFavoriteHeaderCell(),
          <Table.HeaderCell verticalAlign={"middle"} key={1}>{getMessage("code")}</Table.HeaderCell>,
          <Table.HeaderCell verticalAlign={"middle"} key={2}>{getMessage("description")}</Table.HeaderCell>
        ];
      }
    }

    return (
        <Table.Row>
          {headerColumns}
        </Table.Row>
    )
  };

  renderTableBody = (procedureType?: ProcedureCategory): React.ReactNode => {

    const {
      procedures,
      onSelectProcedure,
      getMedicamentsChanges,
      getMessage
    } = this.props;

    const {selectedProcedure} = this.state;

    const numberFormat = new Intl.NumberFormat("de-CH", {minimumFractionDigits: 2});

    let rows: Array<React.ReactNode>;

    let medicamentsChanges: MedicamentChanges = {};

    switch (procedureType) {
      case ProcedureCategory.MEDICAMENT:
      case ProcedureCategory.MATERIAL: {

        medicamentsChanges = getMedicamentsChanges();

        rows = procedures.map((procedure, index) => {

          const quantityChange = medicamentsChanges[procedure.id!] || 0;
          const quantity = (procedure.quantity || 0) + quantityChange;

          return (
              <Table.Row
                  key={index}
                  onClick={() => this.markProcedure(procedure.id)}
                  onDoubleClick={() => onSelectProcedure(procedure)}
                  active={procedure.id == selectedProcedure}
              >
                {this.renderFavoriteCell(procedure)}
                <Table.Cell>{procedure.code}</Table.Cell>
                <Table.Cell>{getMessage(procedure.description!)}</Table.Cell>
                <Table.Cell textAlign="left">{numberFormat.format(quantity)}</Table.Cell>
              </Table.Row>
          )
        });

        break;
      }
      case ProcedureCategory.BLOCK_PROCEDURE: {

        rows = procedures.map((procedure, index) => {
          return (
              <Table.Row
                  key={index}
                  onClick={() => this.markProcedure(procedure.id)}
                  onDoubleClick={() => onSelectProcedure(procedure)}
                  active={procedure.id == selectedProcedure}
              >
                {this.renderFavoriteCell(procedure)}
                <Table.Cell>{getMessage(procedure.description!)}</Table.Cell>
              </Table.Row>
          )
        });

        break;
      }
      case ProcedureCategory.COST_ESTIMATION: {

        rows = procedures.map((procedure, index) => {

          let childRows: Array<React.ReactNode> = [];
          if (!!procedure.childProcedures) {
            childRows = procedure.childProcedures.map((child, index1) => (
                <Table.Row
                    key={index + index1 + 1}
                    disabled
                >
                  <Table.Cell textAlign="left">{numberFormat.format(child.quantity!)}</Table.Cell>
                  <Table.Cell>{getMessage(child.description!)}</Table.Cell>
                </Table.Row>
            ))
          }

          return (
              <React.Fragment key={index}>
                <Table.Row
                    onClick={() => this.markProcedure(procedure.id)}
                    onDoubleClick={() => onSelectProcedure(procedure)}
                    active={procedure.id == selectedProcedure}
                    disabled={procedure.quantity == 0}
                >
                  <Table.Cell textAlign="left">{numberFormat.format(procedure.quantity!)}</Table.Cell>
                  <Table.Cell>{getMessage(procedure.description!)}</Table.Cell>
                </Table.Row>
                {childRows}
              </React.Fragment>
          )
        });

        break;
      }
      default: {

        rows = procedures.map((procedure, index) => {
          return (
              <Table.Row
                  key={index}
                  onClick={() => this.markProcedure(procedure.id)}
                  onDoubleClick={() => onSelectProcedure(procedure)}
                  active={procedure.id == selectedProcedure}
              >
                {this.renderFavoriteCell(procedure)}
                <Table.Cell>{procedure.code}</Table.Cell>
                <Table.Cell>{getMessage(procedure.description!)}</Table.Cell>
              </Table.Row>
          )
        });
      }
    }

    return (
        <React.Fragment>
          {rows}
        </React.Fragment>
    );
  };

}

export default withMessages(withEntriesContext(ProceduresTableSegment));
